import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getUserInstances,
  groupInstancesByOrganization,
} from '@frontend/api/services/instancesService';
import { isDesktopApp } from '@frontend/utils/desktop';

// Feeds the desktop shell's title-bar instance switcher. That chrome runs in a
// separate process with no auth, so it can't fetch the instance list itself —
// the web app pushes it (and the current instance) over the desktop bridge
// whenever either changes. No-op on the web build and on shells too old to
// expose `setInstances`.
export function useDesktopInstanceBar() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const queryClient = useQueryClient();
  const { data: instances } = useQuery({
    queryKey: ['user-instances'],
    queryFn: getUserInstances,
    enabled: isDesktopApp(),
  });

  // Clicking a title-bar switcher asks us to re-fetch: invalidating refetches
  // the list, and the effect below pushes the fresh groups back to the shell.
  // The menu opens instantly off the shell's cache; this updates the next open.
  useEffect(() => {
    if (!isDesktopApp()) {
      return;
    }
    return window.desktopAPI?.onRefreshInstances?.(() => {
      void queryClient.invalidateQueries({ queryKey: ['user-instances'] });
    });
  }, [queryClient]);

  useEffect(() => {
    if (!isDesktopApp() || !instances) {
      return;
    }
    const grouped = groupInstancesByOrganization(instances);
    const groups = Array.from(grouped, ([org, insts]) => ({
      org,
      instances: insts.map(inst => ({
        id: inst.instance_id,
        name: inst.instance_nickname || inst.instance_name,
      })),
    }));
    void window.desktopAPI?.app?.setInstances?.({
      groups,
      currentInstanceId: instanceId || '',
    });
  }, [instances, instanceId]);
}
